import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImagePlus, Pencil, Plus, Search as SearchIcon, Trash2, X } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import {
  listSisAssetStores,
  getSisStore,
  updateSisStoreProfile,
  uploadSisStorePhotos,
  deleteSisStorePhoto,
  MAX_STORE_PHOTOS,
  deleteSisAsset,
  type SisAssetStore,
  type SisAsset,
  type SisStoreProfile,
  type SisStoreProfilePayload,
} from '@/api/sisAssets';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { SisAssetModal } from './SisAssetModal';

export function SisAssetsMasterPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  const storesQ = useQuery({ queryKey: ['sis-asset-stores'], queryFn: () => listSisAssetStores() });

  if (selected == null) {
    return (
      <StoreList
        stores={storesQ.data ?? []}
        loading={storesQ.isFetching && !storesQ.data}
        onOpen={setSelected}
      />
    );
  }
  return <StoreDetail partyId={selected} onBack={() => setSelected(null)} qc={qc} />;
}

function StoreList({
  stores,
  loading,
  onOpen,
}: {
  stores: SisAssetStore[];
  loading: boolean;
  onOpen: (id: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const groups = useMemo(
    () => Array.from(new Set(stores.map((s) => s.group).filter(Boolean))).sort(),
    [stores],
  );

  // Filter by group + a free-text search over the visible fields, so the count and
  // pagination reflect the actual matches (search must run here, before paging).
  const filtered = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return stores.filter((s) => {
      if (group && s.group !== group) return false;
      if (!terms.length) return true;
      const hay = [s.name, s.group, s.city, s.lengthFt, s.widthFt, s.areaSqft, s.salesman]
        .filter((v) => v != null && v !== '')
        .join(' ')
        .toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [stores, group, search]);

  useEffect(() => { setPage(1); }, [search, group]);
  const total = filtered.length;
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <SectionHeader
        title="SIS Assets Master"      />

      <FilterBar>
        <FilterField label="Search" className="sm:col-auto">
          <div className="relative w-full">
            <SearchIcon
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              title="Search by store, city or salesman"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search SIS stores"
            />
          </div>
        </FilterField>
        <FilterField label="Group">
          <Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All Groups</option>
            {groups.map((g) => <option key={g}>{g}</option>)}
          </Select>
        </FilterField>
      </FilterBar>

      <Table<SisAssetStore>
        storageKey="sis-asset-stores"
        title="SIS Stores"
        count={`${total} stores`}
        rows={pageRows}
        rowKey={(r) => r.id}
        loading={loading}
        footer={
          <Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
        }
        columns={[
          { key: 'name', header: 'Store', cell: (r) => <span className="font-extrabold text-accent">{r.name}</span> },
          { key: 'group', header: 'Group', cell: (r) => (r.group ? <Badge tone="muted">{r.group}</Badge> : '—') },
          { key: 'city', header: 'City', cell: (r) => r.city || '—' },
          { key: 'dims', header: 'L × W (ft)', cell: (r) => (r.lengthFt && r.widthFt ? `${r.lengthFt} × ${r.widthFt}` : '—') },
          { key: 'area', header: 'Area (sq ft)', align: 'right', cell: (r) => (r.areaSqft != null ? r.areaSqft : '—') },
          { key: 'salesman', header: 'Salesman', cell: (r) => r.salesman || '—' },
          { key: 'assets', header: 'Asset lines', align: 'right', cell: (r) => <strong>{r.assetLines}</strong> },
          {
            key: 'go',
            header: '',
            align: 'right',
            cell: (r) => (
              <Button size="sm" variant="glass" onClick={() => onOpen(r.id)}>Manage →</Button>
            ),
          },
        ]}
      />
    </div>
  );
}

function StoreDetail({
  partyId,
  onBack,
  qc,
}: {
  partyId: number;
  onBack: () => void;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const confirmDlg = useConfirm();
  const detailQ = useQuery({ queryKey: ['sis-store', partyId], queryFn: () => getSisStore(partyId) });
  const [assetModal, setAssetModal] = useState(false);
  const [editing, setEditing] = useState<SisAsset | null>(null);

  const store = detailQ.data?.store;
  const assets = detailQ.data?.assets ?? [];

  const delAsset = useMutation({
    mutationFn: (id: number) => deleteSisAsset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-store', partyId] });
      qc.invalidateQueries({ queryKey: ['sis-asset-stores'] });
    },
  });

  return (
    <div>
      <SectionHeader title={store ? `${store.name} — Assets` : 'Store Assets'} />

      <div className="mb-2 flex">
        <button type="button" onClick={onBack} className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs">
          <ArrowLeft size={12} /> All stores
        </button>
      </div>

      {store && <StoreProfileCard partyId={partyId} store={store} qc={qc} />}

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-extrabold">Assets</h3>
          <Button size="sm" variant="primary" icon={<Plus size={12} />} onClick={() => { setEditing(null); setAssetModal(true); }}>
            Add asset
          </Button>
        </div>
        <Table<SisAsset>
          storageKey="sis-assets"
          title="Fixtures in store"
          count={`${assets.length} lines`}
          rows={assets}
          rowKey={(r) => String(r.id)}
          loading={detailQ.isFetching && !detailQ.data}
          columns={[
            { key: 'type', header: 'Asset', cell: (r) => <strong>{r.assetType}</strong> },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => r.quantity },
            { key: 'material', header: 'Material', cell: (r) => r.material || '—' },
            { key: 'size', header: 'Size', cell: (r) => r.size || '—' },
            { key: 'remarks', header: 'Remarks', cell: (r) => r.remarks || '—' },
            {
              key: 'custom',
              header: 'Custom',
              cell: (r) =>
                r.customFields.length ? (
                  <span className="flex flex-wrap gap-1">
                    {r.customFields.map((c, i) => (
                      <Badge key={i} tone="muted">{c.label}: {c.value}</Badge>
                    ))}
                  </span>
                ) : (
                  '—'
                ),
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              cell: (r) => (
                <span className="inline-flex gap-1">
                  <button onClick={() => { setEditing(r); setAssetModal(true); }} className="rounded p-1.5 text-[var(--text-muted)] hover:bg-accent/10 hover:text-accent" aria-label="Edit">
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={async () => { if (await confirmDlg(`Delete ${r.quantity} × ${r.assetType}?`)) delAsset.mutate(r.id); }}
                    className="rounded p-1.5 text-[var(--text-muted)] hover:bg-danger/10 hover:text-danger"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </span>
              ),
            },
          ]}
        />
      </div>

      {store && (
        <SisAssetModal open={assetModal} partyId={partyId} editing={editing} onClose={() => setAssetModal(false)} />
      )}
    </div>
  );
}

function StoreProfileCard({
  partyId,
  store,
  qc,
}: {
  partyId: number;
  store: SisStoreProfile;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState<SisStoreProfilePayload>({});

  useEffect(() => {
    setF({
      lengthFt: store.lengthFt ?? undefined,
      widthFt: store.widthFt ?? undefined,
      areaSqft: store.areaSqft ?? undefined,
      salesman: store.salesman,
      salary: store.salary ?? undefined,
      address: store.address,
      city: store.city,
    });
  }, [store]);

  const save = useMutation({
    mutationFn: () => updateSisStoreProfile(partyId, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-store', partyId] });
      qc.invalidateQueries({ queryKey: ['sis-asset-stores'] });
      setEdit(false);
    },
  });

  const autoArea = useMemo(
    () => (f.lengthFt && f.widthFt ? Math.round(f.lengthFt * f.widthFt * 100) / 100 : undefined),
    [f.lengthFt, f.widthFt],
  );

  if (!edit) {
    return (
      <div className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-3 sm:px-5 sm:py-3.5">
          <h3 className="text-sm font-bold">Store profile</h3>
          <Button size="sm" variant="glass" icon={<Pencil size={12} />} onClick={() => setEdit(true)}>Edit</Button>
        </div>
        <StorePhotos partyId={partyId} photoUrls={store.photoUrls} qc={qc} />
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 text-sm sm:grid-cols-4 sm:p-5">
          <Field label="Length (ft)" value={store.lengthFt ?? '—'} />
          <Field label="Width (ft)" value={store.widthFt ?? '—'} />
          <Field label="Area (sq ft)" value={store.areaSqft ?? '—'} />
          <Field label="Salesman" value={store.salesman || '—'} />
          <Field label="Salary" value={store.salary != null ? `₹${formatINR(store.salary)}` : '—'} />
          <Field label="City" value={store.city || '—'} />
          <div className="col-span-2">
            <Field label="Address" value={store.address || '—'} />
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
      <div className="flex items-center border-b border-[var(--border-color)] px-3 py-3 sm:px-5 sm:py-3.5">
        <h3 className="text-sm font-bold">Edit store profile</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 sm:p-5">
        <FormGroup label="Length (ft)">
          <Input type="number" value={f.lengthFt ?? ''} onChange={(e) => setF({ ...f, lengthFt: e.target.value === '' ? undefined : Number(e.target.value) })} className="text-right" />
        </FormGroup>
        <FormGroup label="Width (ft)">
          <Input type="number" value={f.widthFt ?? ''} onChange={(e) => setF({ ...f, widthFt: e.target.value === '' ? undefined : Number(e.target.value) })} className="text-right" />
        </FormGroup>
        <FormGroup label="Area (sq ft)" hint={autoArea != null ? `auto: ${autoArea}` : undefined}>
          <Input type="number" value={f.areaSqft ?? ''} onChange={(e) => setF({ ...f, areaSqft: e.target.value === '' ? undefined : Number(e.target.value) })} className="text-right" placeholder={autoArea != null ? String(autoArea) : ''} />
        </FormGroup>
        <FormGroup label="Salary">
          <Input type="number" value={f.salary ?? ''} onChange={(e) => setF({ ...f, salary: e.target.value === '' ? undefined : Number(e.target.value) })} className="text-right" />
        </FormGroup>
        <FormGroup label="Salesman" className="sm:col-span-2">
          <Input value={f.salesman ?? ''} onChange={(e) => setF({ ...f, salesman: e.target.value })} />
        </FormGroup>
        <FormGroup label="City" className="sm:col-span-2">
          <Input value={f.city ?? ''} onChange={(e) => setF({ ...f, city: e.target.value })} />
        </FormGroup>
        <FormGroup label="Address" className="col-span-2 sm:col-span-4">
          <Input value={f.address ?? ''} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </FormGroup>
      </div>
      <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-4 py-3 sm:px-5">
        <Button variant="glass" onClick={() => setEdit(false)}>Cancel</Button>
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </div>
  );
}

function StorePhotos({
  partyId,
  photoUrls,
  qc,
}: {
  partyId: number;
  photoUrls: string[];
  qc: ReturnType<typeof useQueryClient>;
}) {
  const toast = useToast();
  const confirmDlg = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const left = MAX_STORE_PHOTOS - photoUrls.length;

  const upload = useMutation({
    mutationFn: (files: (File | Blob)[]) => uploadSisStorePhotos(partyId, files),
    onSuccess: (_r, files) => {
      qc.invalidateQueries({ queryKey: ['sis-store', partyId] });
      toast.push(`${files.length} photo${files.length === 1 ? '' : 's'} added`, 'success');
    },
    onError: (e) => toast.push(apiError(e, 'Could not upload photo'), 'error'),
  });
  const remove = useMutation({
    mutationFn: (url: string) => deleteSisStorePhoto(partyId, url),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sis-store', partyId] }),
    onError: (e) => toast.push(apiError(e, 'Could not remove photo'), 'error'),
  });

  // Up to MAX_STORE_PHOTOS in total — extra images beyond the free slots are skipped.
  const take = (list: (File | Blob)[]) => {
    const images = list.filter((f) => f.type?.startsWith('image/'));
    if (!images.length) { toast.push('Please choose an image file', 'error'); return; }
    if (left <= 0) { toast.push(`A store can have up to ${MAX_STORE_PHOTOS} photos — remove one first`, 'error'); return; }
    if (images.length > left) toast.push(`Only ${left} more photo${left === 1 ? '' : 's'} allowed — adding the first ${left}`, 'warning');
    upload.mutate(images.slice(0, left));
  };

  // Paste images from anywhere on the page (Ctrl+V). Only intercepts image
  // clipboard content — text/other pastes fall through untouched.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.items ?? [])]
        .filter((it) => it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) { e.preventDefault(); take(files); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, left]);

  async function onRemove(url: string) {
    if (await confirmDlg('Remove this photo?')) remove.mutate(url);
  }

  return (
    <div className="px-4 pt-4 sm:px-5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); take([...(e.dataTransfer?.files ?? [])]); }}
        onClick={() => { if (!photoUrls.length) fileRef.current?.click(); }}
        className={`relative rounded-card border border-dashed ${
          dragOver ? 'border-accent ring-2 ring-accent/40' : 'border-[var(--border-color)]'
        } bg-[var(--bg-subtle,transparent)] ${photoUrls.length ? 'p-2' : 'cursor-pointer'}`}
      >
        {photoUrls.length ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {photoUrls.map((url, i) => (
              <div key={url} className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-[var(--border-color)]">
                <a href={url} target="_blank" rel="noreferrer" title="Open full size">
                  <img src={url} alt={`Store photo ${i + 1}`} className="h-full w-full object-cover" />
                </a>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); void onRemove(url); }}
                  disabled={remove.isPending}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white backdrop-blur hover:bg-danger"
                  aria-label="Remove photo"
                  title="Remove photo"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {left > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--border-color)] text-[var(--text-muted)] hover:border-accent hover:text-accent"
              >
                <ImagePlus size={20} className="text-accent" />
                <span className="text-xs font-medium">{upload.isPending ? 'Uploading…' : 'Add photo'}</span>
                <span className="text-[10px]">{photoUrls.length}/{MAX_STORE_PHOTOS}</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-[var(--text-muted)]">
            <ImagePlus size={26} className="text-accent" />
            <p className="text-sm font-medium">{upload.isPending ? 'Uploading…' : 'Add photos of the store'}</p>
            <p className="text-xs">Click to upload, paste (Ctrl+V), or drop images here · up to {MAX_STORE_PHOTOS} photos</p>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { take([...(e.target.files ?? [])]); e.currentTarget.value = ''; }}
        />
      </div>
      {photoUrls.length > 0 && (
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">
          {photoUrls.length}/{MAX_STORE_PHOTOS} photos · click a photo to open it · add more by clicking, pasting (Ctrl+V) or dropping images
        </p>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <dt className="text-xs text-[var(--text-muted)]">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
